<template>
  <div class="page-container">
    <h1 class="page-title">{{ t('pages.applications.power.title') }}</h1>
    
    <!-- 引言部分 -->
    <div class="content-section">
      <div class="section-header">
        <h2 class="section-title">{{ t('pages.applications.power.introduction.title') }}</h2>
      </div>
      <div class="introduction-wrapper">
        <div 
          class="text-block" 
          :class="{ 'collapsed': !isIntroductionExpanded }"
          v-html="renderContent(t('pages.applications.power.introduction.content'))"
        ></div>
        <button 
          class="expand-btn" 
          @click="toggleIntroduction"
          :aria-expanded="isIntroductionExpanded"
        >
          <span class="expand-text">{{ isIntroductionExpanded ? t('buttons.collapse') : t('buttons.expand') }}</span>
          <svg class="expand-icon" :class="{ 'rotated': isIntroductionExpanded }" width="16" height="16" viewBox="0 0 16 16">
            <path d="M8 12L2 6h12l-6 6z" fill="currentColor"/>
          </svg>
        </button>
      </div>
    </div>
    
    <div class="decorative-divider"></div>
    
    <!-- 应用案例部分 -->
    <div class="content-section">
      <div class="section-header">
        <h2 class="section-title">{{ t('pages.applications.power.cases.title') }}</h2>
      </div>
      
      <div class="applications-list">
        <div class="application-item" v-for="(item, index) in applicationCases" :key="index">
          <h3 class="app-title">{{ item.title }}</h3>
          <div class="app-content">
            <div class="app-text" v-html="renderContent(item.content)"></div>
            <div class="app-image" v-if="item.image">
              <img :src="getImageUrl(item.image)" :alt="item.title">
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLangStore } from '@/pinia/stores/lang';
import zhData from '@/i18n/zh';
import enData from '@/i18n/en';

const { t } = useI18n();
const langStore = useLangStore();

// 引言展开状态
const isIntroductionExpanded = ref(false);

// 切换引言展开状态
const toggleIntroduction = () => {
  isIntroductionExpanded.value = !isIntroductionExpanded.value;
};

// 获取翻译数据
const translationData = computed(() => {
  return langStore.currentLang.startsWith('zh') ? zhData : enData;
});

// 获取所有应用案例
const applicationCases = computed(() => {
  // 从i18n获取数据
  const data = translationData.value.pages.applications.power.cases.list;
  return data as {
    title: string;
    content: string;
    image: string;
  }[];
});

// 处理图片路径
const getImageUrl = (path: string) => {
  // 如果图片不存在，使用默认图片
  return path || '/images/default.jpg';
}

// 渲染带有换行的内容
const renderContent = (content: string) => {
  if (!content) return '';
  return content.split('\n\n').map(paragraph => `<p>${paragraph}</p>`).join('');
};
</script>

<style scoped lang="scss">
@import '@/assets/styles/content-blocks.scss';

.page-container {
  max-width: 1200px;
  margin: 0 auto;
  overflow: auto;
}

@media screen and (max-width: 768px) {
  .page-container {
    padding: 30px 15px;
  }
}

/* 超小屏幕优化 - 修复application-item溢出 */
@media screen and (max-width: 480px) {
  .page-container {
    padding: 20px 10px;
  }
  
  .page-title {
    font-size: 20px;
    margin-bottom: 30px;
  }
  
  .section-header .section-title {
    font-size: 18px;
  }
  
  .applications-list .application-item {
    padding: 15px;
    margin-bottom: 30px;
    
    .app-title {
      font-size: 16px;
      margin-bottom: 15px;
    }
    
    .app-content {
      gap: 15px;
      
      .app-text {
        min-width: unset;
        width: 100%;
        
        p {
          font-size: 14px;
          text-indent: 1.5em;
          margin-bottom: 8px;
        }
      }
      
      .app-image {
        min-width: unset;
        width: 100%;
        
        img {
          max-width: 100%;
          width: 100%;
        }
      }
    }
  }
  
  .expand-btn {
    padding: 10px 20px;
    font-size: 13px;
    margin-top: 15px;
  }
  
  .text-block {
    &.collapsed {
      max-height: 120px;
    }
  }
}

// 引言展开/收起样式
.introduction-wrapper {
  position: relative;
}

.text-block {
  transition: max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease;
  overflow: hidden;
  
  &.collapsed {
    max-height: 150px;
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 60px;
      // background: linear-gradient(transparent, rgba(255, 255, 255, 0.7) 20%, rgba(189, 215, 238, 1) 100%);
      pointer-events: none;
    }
  }
}

.expand-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
  padding: 12px 24px;
  border: none;
  border-radius: 25px;
  background: linear-gradient(135deg, #667eea 0%, #4f9be1 100%);
  color: white;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s ease;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
    
    &::before {
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 10px rgba(102, 126, 234, 0.3);
  }
  
  .expand-text {
    position: relative;
    z-index: 1;
  }
  
  .expand-icon {
    position: relative;
    z-index: 1;
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
    
    &.rotated {
      transform: rotate(180deg);
    }
  }
}
</style> 